import { GridColDef, GridSortModel, DataGrid } from "@mui/x-data-grid";
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Tooltip,
    Badge,
    List,
    ListItem,
    ListItemText,
    Divider,
    InputAdornment,
    CircularProgress,
    Backdrop,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Add as AddIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Assignment as AssignmentIcon,
    LocalShipping as ShippingIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    FilterList as FilterListIcon,
    Close as CloseIcon,
    Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { MobilePaginationControls } from "../../components/common/MobilePaginationControls";
import {
    procurementService,
    Purchase,
    PurchaseListRow,
    ReceiveItemPayload,
} from "../../services/procurementService";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Supplier } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import telegramService from "../../services/telegramService";
import { PurchaseOrderDetailDrawer } from "../../components/procurement/PurchaseOrderDetailDrawer";
import {
    PurchaseOrderAdvancedSearch,
    PurchaseOrderSearchParams,
} from "../../components/procurement/PurchaseOrderAdvancedSearch";
import { compareByProductName } from "../../utils/procurementSort";

// ── URL <-> filter state (de)serialization ────────────────────────────────
// Keeps the list's search/filter/sort/page in the URL so navigating away
// (create/edit/view a PO) and back restores exactly where the user was,
// instead of resetting to a blank list every time.
const parsePurchaseSearchParamsFromUrl = (
    params: URLSearchParams,
): PurchaseOrderSearchParams => {
    const result: PurchaseOrderSearchParams = {
        skip: 0,
        limit: 50,
        sort_by: params.get("sort_by") || "expected_delivery_date",
        sort_order: (params.get("sort_order") as "asc" | "desc") || "desc",
    };
    const status = params.get("status");
    if (status) result.status = status;
    const supplierId = params.get("supplier_id");
    if (supplierId) result.supplier_id = supplierId;
    const productIds = params.get("product_ids");
    if (productIds) result.product_ids = productIds.split(",").filter(Boolean);
    const dateFrom = params.get("date_from");
    if (dateFrom) result.date_from = dateFrom;
    const dateTo = params.get("date_to");
    if (dateTo) result.date_to = dateTo;
    const expFrom = params.get("expected_delivery_from");
    if (expFrom) result.expected_delivery_from = expFrom;
    const expTo = params.get("expected_delivery_to");
    if (expTo) result.expected_delivery_to = expTo;
    const ref = params.get("reference_number");
    if (ref) result.reference_number = ref;
    return result;
};

const serializePurchaseSearchParamsToUrl = (
    searchParams: PurchaseOrderSearchParams,
    q: string,
    page: number,
): URLSearchParams => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (searchParams.status) params.set("status", searchParams.status);
    if (searchParams.supplier_id)
        params.set("supplier_id", searchParams.supplier_id);
    if (searchParams.product_ids && searchParams.product_ids.length > 0)
        params.set("product_ids", searchParams.product_ids.join(","));
    if (searchParams.date_from) params.set("date_from", searchParams.date_from);
    if (searchParams.date_to) params.set("date_to", searchParams.date_to);
    if (searchParams.expected_delivery_from)
        params.set(
            "expected_delivery_from",
            searchParams.expected_delivery_from,
        );
    if (searchParams.expected_delivery_to)
        params.set("expected_delivery_to", searchParams.expected_delivery_to);
    if (searchParams.reference_number)
        params.set("reference_number", searchParams.reference_number);
    if (
        searchParams.sort_by &&
        searchParams.sort_by !== "expected_delivery_date"
    )
        params.set("sort_by", searchParams.sort_by);
    if (searchParams.sort_order && searchParams.sort_order !== "desc")
        params.set("sort_order", searchParams.sort_order);
    if (page > 0) params.set("page", String(page));
    return params;
};

// Grid columns the server can sort by (the rest are display-only).
const SERVER_SORTABLE = new Set(["total_amount", "status", "expected_delivery_date"]);
const DEFAULT_SORT = { sort_by: "expected_delivery_date", sort_order: "desc" as const };
const MOBILE_PAGE_SIZE = 10;

const PurchaseOrderList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Passed as router state whenever navigating to create/edit/view, so
    // those pages' Back/Cancel buttons can return here with filters intact
    // instead of a hardcoded bare path that would lose them.
    const backLocationState = {
        from: `${location.pathname}${location.search}`,
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // One page of lean rows from the server — filtering, sorting and
    // paging all happen in the database (see searchPurchases).
    const [purchases, setPurchases] = useState<PurchaseListRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);
    const [loading, setLoading] = useState(true);
    // Fetching a full order for a row action (details, receive).
    const [actionBusy, setActionBusy] = useState(false);

    // URL is the source of truth for initial state — read once on mount.
    const [urlQuery, setUrlQuery] = useSearchParams();
    const [initialUrlState] = useState(() => ({
        q: urlQuery.get("q") || "",
        page: Number(urlQuery.get("page")) || 0,
        searchParams: parsePurchaseSearchParamsFromUrl(urlQuery),
    }));

    // Search and filter state
    const [searchInput, setSearchInput] = useState(initialUrlState.q);
    const [debouncedSearch, setDebouncedSearch] = useState(initialUrlState.q);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [searchParams, setSearchParams] = useState<PurchaseOrderSearchParams>(
        initialUrlState.searchParams,
    );
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);

    // Pagination — one page number driving whichever view (mobile/desktop) is active.
    const [paginationModel, setPaginationModel] = useState({
        page: initialUrlState.page,
        pageSize: 50,
    });

    // Dialog / drawer state
    const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(
        null,
    );
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [detailPurchase, setDetailPurchase] = useState<Purchase | null>(null);
    const [detailPurchaseIndex, setDetailPurchaseIndex] = useState<number>(-1);
    const [isReceiveDialogOpen, setIsReceiveDialogOpen] = useState(false);
    const [receiveQuantities, setReceiveQuantities] = useState<
        Record<string, number>
    >({});
    // Optional per-line expiry date — left null for anything staff don't
    // need to track. Keyed by product_id, same as receiveQuantities.
    const [receiveExpiryDates, setReceiveExpiryDates] = useState<
        Record<string, Date | null>
    >({});
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : paginationModel.pageSize;
    const page = paginationModel.page;

    // Only the newest request may update the list (typing fast, paging).
    const requestId = useRef(0);
    const loadPurchases = useCallback(async () => {
        const id = ++requestId.current;
        try {
            setLoading(true);
            const result = await procurementService.searchPurchases({
                skip: page * pageSize,
                limit: pageSize,
                q: debouncedSearch.trim() || undefined,
                status: searchParams.status || undefined,
                supplier_id: searchParams.supplier_id || undefined,
                product_ids: searchParams.product_ids,
                date_from: searchParams.date_from || undefined,
                date_to: searchParams.date_to || undefined,
                expected_delivery_from: searchParams.expected_delivery_from || undefined,
                expected_delivery_to: searchParams.expected_delivery_to || undefined,
                reference_number: searchParams.reference_number || undefined,
                sort_by: searchParams.sort_by || DEFAULT_SORT.sort_by,
                sort_order: searchParams.sort_order || DEFAULT_SORT.sort_order,
            });
            if (id !== requestId.current) return;
            setPurchases(result.items);
            setTotalCount(result.total);
            setOverdueCount(result.overdue_count);
        } catch (error) {
            if (id !== requestId.current) return;
            console.error("Error loading purchases:", error);
            showSnackbar("Failed to load purchase orders", "error");
        } finally {
            if (id === requestId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, debouncedSearch, searchParams]);

    useEffect(() => {
        loadPurchases();
    }, [loadPurchases]);

    // Suppliers for the filter dropdown — once.
    useEffect(() => {
        procurementService
            .getSuppliers()
            .then(setSuppliers)
            .catch((error) => console.error("Error loading suppliers:", error));
    }, []);

    // Debounce search input — only updates debouncedSearch after 400 ms of inactivity
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchInput), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const showSnackbar = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    // A new search or filter starts again from the first page. Compared by
    // value, so mounting (or React re-running effects) never resets a page
    // restored from the URL.
    const filterKey = JSON.stringify([debouncedSearch, searchParams]);
    const lastFilterKey = useRef(filterKey);
    useEffect(() => {
        if (lastFilterKey.current === filterKey) return;
        lastFilterKey.current = filterKey;
        setPaginationModel((m) => ({ ...m, page: 0 }));
    }, [filterKey]);

    const currentPage = page;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    // Grid header sorting maps onto the same server sort as the Filters dialog.
    const sortModel: GridSortModel = SERVER_SORTABLE.has(searchParams.sort_by || "")
        ? [{ field: searchParams.sort_by!, sort: searchParams.sort_order || "desc" }]
        : [];
    const handleSortModelChange = (model: GridSortModel) => {
        const next = model[0];
        setSearchParams((prev) => ({
            ...prev,
            sort_by: next ? next.field : DEFAULT_SORT.sort_by,
            sort_order: next ? (next.sort ?? "desc") : DEFAULT_SORT.sort_order,
        }));
    };

    // Reflect filters/search/page into the URL so navigating away (create,
    // edit, view a PO) and back restores this exact view.
    useEffect(() => {
        const params = serializePurchaseSearchParamsToUrl(
            searchParams,
            debouncedSearch,
            currentPage,
        );
        setUrlQuery(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, debouncedSearch, currentPage]);

    // Count active filters
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (searchParams.status) count++;
        if (searchParams.supplier_id) count++;
        if (searchParams.product_ids && searchParams.product_ids.length > 0)
            count++;
        if (searchParams.date_from || searchParams.date_to) count++;
        if (
            searchParams.expected_delivery_from ||
            searchParams.expected_delivery_to
        )
            count++;
        if (searchParams.reference_number) count++;
        return count;
    }, [searchParams]);

    const clearAllFilters = () => {
        setSearchInput("");
        setDebouncedSearch("");
        setSearchParams({
            skip: 0,
            limit: 50,
            sort_by: "expected_delivery_date",
            sort_order: "desc",
        });
        setPaginationModel((m) => ({ ...m, page: 0 }));
    };

    const handleAdvancedSearch = (params: PurchaseOrderSearchParams) => {
        setSearchParams(params);
    };

    const handleReceiveItems = async () => {
        if (!selectedPurchase) return;

        try {
            const itemReceipts: Record<string, ReceiveItemPayload> = {};
            Object.entries(receiveQuantities).forEach(([productId, quantity]) => {
                const expiry = receiveExpiryDates[productId];
                itemReceipts[productId] = {
                    quantity,
                    expiry_date: expiry ? expiry.toISOString() : undefined,
                };
            });
            await procurementService.receivePurchaseItems(
                selectedPurchase.purchase_id,
                itemReceipts,
            );
            showSnackbar("Items received successfully", "success");
            setIsReceiveDialogOpen(false);
            setSelectedPurchase(null);
            setReceiveQuantities({});
            setReceiveExpiryDates({});
            loadPurchases();
        } catch (error) {
            console.error("Error receiving items:", error);
            showSnackbar("Failed to receive items", "error");
        }
    };

    const openReceiveDialog = (purchase: Purchase) => {
        setSelectedPurchase(purchase);
        // Initialize receive quantities with remaining quantities
        const quantities: Record<string, number> = {};
        purchase.items.forEach((item) => {
            quantities[item.product_id] = item.quantity_remaining || 0;
        });
        setReceiveQuantities(quantities);
        setReceiveExpiryDates({});
        setIsReceiveDialogOpen(true);
    };

    // List rows are lean; anything that needs the order's lines loads it.
    const withFullPurchase = async (id: string, action: (full: Purchase) => void) => {
        try {
            setActionBusy(true);
            action(await procurementService.getPurchase(id));
        } catch {
            showSnackbar("Failed to load purchase order details", "error");
        } finally {
            setActionBusy(false);
        }
    };

    const handleViewDetail = (id: string) =>
        withFullPurchase(id, (full) => {
            setDetailPurchase(full);
            setDetailPurchaseIndex(purchases.findIndex((p) => p.purchase_id === id));
            setDetailDrawerOpen(true);
        });

    const openReceiveForRow = (id: string) => withFullPurchase(id, openReceiveDialog);

    const handleNavigateDetail = useCallback(
        async (direction: "prev" | "next") => {
            const nextIndex =
                direction === "prev"
                    ? detailPurchaseIndex - 1
                    : detailPurchaseIndex + 1;
            if (nextIndex < 0 || nextIndex >= purchases.length) return;
            const target = purchases[nextIndex];
            // Rows on this page only; the drawer needs the full order.
            try {
                const full = await procurementService.getPurchase(
                    target.purchase_id,
                );
                setDetailPurchaseIndex(nextIndex);
                setDetailPurchase(full);
            } catch {
                showSnackbar("Failed to load purchase order details", "error");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [detailPurchaseIndex, purchases],
    );

    const handlePaymentAdded = (updated: Purchase) => {
        setDetailPurchase(updated);
        // Refresh this page's totals/payment status from the server.
        loadPurchases();
    };

    const handleSendToTelegram = async (purchase: Purchase) => {
        try {
            await telegramService.sendOrderToGroup(purchase);
            showSnackbar("Order sent to Telegram successfully!", "success");
        } catch (error) {
            showSnackbar(
                error instanceof Error
                    ? error.message
                    : "Failed to send order to Telegram",
                "error",
            );
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING":
                return "warning";
            case "CONFIRMED":
                return "info";
            case "RECEIVED":
                return "success";
            case "PARTIAL_RECEIVED":
                return "primary";
            case "CANCELLED":
                return "error";
            case "ON_HOLD":
                return "default";
            default:
                return "default";
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "PENDING":
                return <ScheduleIcon fontSize="small" />;
            case "CONFIRMED":
                return <AssignmentIcon fontSize="small" />;
            case "RECEIVED":
                return <CheckCircleIcon fontSize="small" />;
            case "PARTIAL_RECEIVED":
                return <ShippingIcon fontSize="small" />;
            default:
                return <AssignmentIcon fontSize="small" />;
        }
    };

    const columns: GridColDef[] = [
        {
            field: "supplier",
            headerName: "Supplier",
            flex: 1,
            minWidth: 220,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight="medium">
                        {params.row.supplier?.name || "Unknown Supplier"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {params.row.purchase_number} &middot;{" "}
                        {new Date(
                            params.row.purchase_date,
                        ).toLocaleDateString()}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "items_count",
            headerName: "Items",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View details">
                    <IconButton
                        size="small"
                        onClick={() => handleViewDetail(params.row.purchase_id)}
                        sx={{ p: 0.5 }}
                    >
                        <Badge
                            badgeContent={params.row.item_count}
                            color="primary"
                        >
                            <AssignmentIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>
            ),
        },
        {
            field: "total_amount",
            headerName: "Total (Ordered)",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium" color="primary">
                    {formatCurrencyBDT(
                        Number(
                            params.row.net_payable ?? params.row.total_amount,
                        ),
                    )}
                </Typography>
            ),
        },
        {
            field: "total_received",
            headerName: "Total (Received)",
            width: 150,
            sortable: false,
            renderCell: (params) => {
                const receivedSubtotal = Number(params.row.received_value) || 0;
                const discount = Number(params.row.discount_amount) || 0;
                const tax = Number(params.row.tax_amount) || 0;
                const shipping = Number(params.row.shipping_cost) || 0;
                const receivedTotal =
                    receivedSubtotal - discount + tax + shipping;

                // total_amount is the pure item subtotal (shipping/discount
                // are applied on top, not folded in) — see net_payable.
                const orderedSubtotal = Number(params.row.total_amount);
                const isPartiallyReceived =
                    receivedSubtotal < orderedSubtotal && receivedSubtotal > 0;

                return (
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={
                            isPartiallyReceived
                                ? "success.main"
                                : "text.primary"
                        }
                    >
                        {formatCurrencyBDT(receivedTotal)}
                    </Typography>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: (params) => (
                <Chip
                    icon={getStatusIcon(params.row.status)}
                    label={params.row.status}
                    color={getStatusColor(params.row.status) as any}
                    size="small"
                />
            ),
        },
        {
            field: "expected_delivery_date",
            headerName: "Expected Delivery",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.expected_delivery_date
                        ? new Date(
                              params.row.expected_delivery_date,
                          ).toLocaleDateString()
                        : "Not set"}
                </Typography>
            ),
        },
        {
            field: "actions",
            headerName: "",
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit Purchase Order">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                                `/procurement/purchases/${params.row.purchase_id}/edit`,
                                { state: backLocationState },
                            );
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={3}
                sx={{ flexWrap: "wrap", gap: 1 }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Purchase Orders
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadPurchases} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            navigate("/procurement/purchases/create", {
                                state: backLocationState,
                            })
                        }
                    >
                        {isMobile ? "New PO" : "New Purchase Order"}
                    </Button>
                </Box>
            </Box>

            {/* Search and Filter Bar */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pb: "12px !important" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        {/* Quick Search */}
                        <TextField
                            size="small"
                            placeholder="Search by PO number, supplier, or reference..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{ flex: 1, minWidth: 250 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            fontSize="small"
                                            sx={{ color: "text.secondary" }}
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchInput("");
                                                setDebouncedSearch("");
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />

                        {/* Filter Button with Badge */}
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setAdvancedSearchOpen(true)}
                            color={
                                activeFilterCount > 0 ? "primary" : "inherit"
                            }
                        >
                            Filters
                            {activeFilterCount > 0 && (
                                <Chip
                                    label={activeFilterCount}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1, height: 20, minWidth: 20 }}
                                />
                            )}
                        </Button>

                        {/* Overdue Badge */}
                        {overdueCount > 0 && (
                            <Chip
                                label={`${overdueCount} Overdue`}
                                color="error"
                                size="small"
                                variant="outlined"
                            />
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Results Count */}
                        <Typography variant="body2" color="text.secondary">
                            {activeFilterCount > 0 || debouncedSearch
                                ? `${totalCount} matching`
                                : `${totalCount} orders`}
                        </Typography>

                        {/* Clear All */}
                        {(activeFilterCount > 0 || searchInput) && (
                            <Button
                                size="small"
                                startIcon={<CloseIcon fontSize="small" />}
                                onClick={clearAllFilters}
                                color="inherit"
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Data Grid / Mobile Cards */}
            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : purchases.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No purchase orders found.
                            </Typography>
                        </Box>
                    ) : (
                        purchases.map((p) => (
                            <Card
                                key={p.purchase_id}
                                variant="outlined"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ pb: "8px !important" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                fontWeight="bold"
                                                sx={{ lineHeight: 1.2 }}
                                            >
                                                {p.purchase_number}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {new Date(
                                                    p.purchase_date,
                                                ).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            icon={getStatusIcon(p.status)}
                                            label={p.status.replace("_", " ")}
                                            color={
                                                getStatusColor(p.status) as any
                                            }
                                            size="small"
                                        />
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {p.supplier?.name || "Unknown Supplier"}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mt: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {p.item_count} item
                                            {p.item_count !== 1 ? "s" : ""}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            color="primary"
                                        >
                                            {formatCurrencyBDT(
                                                Number(
                                                    p.net_payable ??
                                                        p.total_amount,
                                                ),
                                            )}
                                        </Typography>
                                    </Box>

                                    {p.expected_delivery_date && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.25 }}
                                        >
                                            Delivery:{" "}
                                            {new Date(
                                                p.expected_delivery_date,
                                            ).toLocaleDateString()}
                                        </Typography>
                                    )}
                                </CardContent>

                                <Divider />

                                <CardActions
                                    sx={{ px: 1.5, py: 0.75, gap: 0.5 }}
                                >
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AssignmentIcon />}
                                        onClick={() =>
                                            handleViewDetail(p.purchase_id)
                                        }
                                        sx={{ flex: 1 }}
                                    >
                                        Details
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() =>
                                            navigate(
                                                `/procurement/purchases/${p.purchase_id}/edit`,
                                                { state: backLocationState },
                                            )
                                        }
                                        sx={{ flex: 1 }}
                                    >
                                        Edit
                                    </Button>
                                    {p.status !== "RECEIVED" &&
                                        p.status !== "CANCELLED" && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="info"
                                                startIcon={<ShippingIcon />}
                                                onClick={() =>
                                                    openReceiveForRow(p.purchase_id)
                                                }
                                                sx={{ flex: 1 }}
                                            >
                                                Receive
                                            </Button>
                                        )}
                                </CardActions>
                            </Card>
                        ))
                    )}
                    <MobilePaginationControls
                        page={page}
                        totalPages={totalPages}
                        canGoNext={page < totalPages - 1}
                        canGoPrev={page > 0}
                        onNext={() =>
                            setPaginationModel((m) => ({ ...m, page: Math.min(m.page + 1, totalPages - 1) }))
                        }
                        onPrev={() =>
                            setPaginationModel((m) => ({ ...m, page: Math.max(m.page - 1, 0) }))
                        }
                        startIndex={page * pageSize}
                        endIndex={Math.min(page * pageSize + purchases.length, totalCount)}
                        total={totalCount}
                    />
                </Box>
            ) : (
                <Card>
                    <DataGrid
                        rows={purchases}
                        columns={columns}
                        loading={loading}
                        getRowId={(row) => row.purchase_id}
                        paginationMode="server"
                        rowCount={totalCount}
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        pageSizeOptions={[25, 50, 100]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        disableRowSelectionOnClick
                        sx={{ border: 0, minHeight: 400 }}
                        hideFooterSelectedRowCount
                    />
                </Card>
            )}

            <Backdrop
                open={actionBusy}
                sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: "common.white" }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <PurchaseOrderDetailDrawer
                open={detailDrawerOpen}
                onClose={() => setDetailDrawerOpen(false)}
                purchase={detailPurchase}
                onEdit={(p) =>
                    navigate(`/procurement/purchases/${p.purchase_id}/edit`, {
                        state: backLocationState,
                    })
                }
                onReceiveItems={(p) => {
                    setDetailDrawerOpen(false);
                    openReceiveDialog(p);
                }}
                onSendToTelegram={handleSendToTelegram}
                onPaymentAdded={handlePaymentAdded}
                onBatchAdded={handlePaymentAdded}
                onNavigate={handleNavigateDetail}
                backLocationState={backLocationState}
                hasPrev={detailPurchaseIndex > 0}
                hasNext={detailPurchaseIndex < purchases.length - 1}
                currentIndex={
                    detailPurchaseIndex >= 0 ? detailPurchaseIndex : undefined
                }
                totalCount={purchases.length}
            />

            {/* Receive Items Dialog */}
            <Dialog
                open={isReceiveDialogOpen}
                onClose={() => setIsReceiveDialogOpen(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pr: 1,
                    }}
                >
                    Receive Purchase Items
                    {isMobile && (
                        <IconButton
                            edge="end"
                            onClick={() => setIsReceiveDialogOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    {selectedPurchase && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {selectedPurchase.purchase_number} -{" "}
                                {selectedPurchase.supplier?.name}
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <List>
                                    {[...selectedPurchase.items]
                                        .sort(compareByProductName)
                                        .map((item) => (
                                        <React.Fragment key={item.item_id}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        (item.product?.name ||
                                                            `Product ${item.product_id}`) +
                                                        (item.is_gift
                                                            ? " (GIFT — free)"
                                                            : "")
                                                    }
                                                    secondary={
                                                        `Ordered: ${item.quantity_ordered}` +
                                                        (item.bonus_quantity
                                                            ? ` | Bonus (free): ${item.bonus_quantity}`
                                                            : "") +
                                                        ` | Received: ${item.quantity_received} | Remaining: ${item.quantity_remaining}`
                                                    }
                                                />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <TextField
                                                        type="number"
                                                        label="Receive Quantity"
                                                        value={
                                                            receiveQuantities[
                                                                item.product_id
                                                            ] || 0
                                                        }
                                                        onChange={(e) =>
                                                            setReceiveQuantities({
                                                                ...receiveQuantities,
                                                                [item.product_id]:
                                                                    parseInt(
                                                                        e.target.value,
                                                                    ) || 0,
                                                            })
                                                        }
                                                        inputProps={{
                                                            min: 0,
                                                            max: item.quantity_remaining,
                                                        }}
                                                        sx={{ width: 150 }}
                                                    />
                                                    <DatePicker
                                                        label="Expiry Date (optional)"
                                                        value={
                                                            receiveExpiryDates[
                                                                item.product_id
                                                            ] ?? null
                                                        }
                                                        onChange={(date) =>
                                                            setReceiveExpiryDates({
                                                                ...receiveExpiryDates,
                                                                [item.product_id]:
                                                                    date,
                                                            })
                                                        }
                                                        slotProps={{
                                                            textField: {
                                                                sx: { width: 180 },
                                                            },
                                                            field: {
                                                                clearable: true,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </LocalizationProvider>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsReceiveDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleReceiveItems} variant="contained">
                        Receive Items
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Advanced Search Dialog */}
            <PurchaseOrderAdvancedSearch
                open={advancedSearchOpen}
                onClose={() => setAdvancedSearchOpen(false)}
                onSearch={handleAdvancedSearch}
                initialParams={searchParams}
                suppliers={suppliers}
            />
        </Box>
    );
};

export default PurchaseOrderList;
